import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { agentService } from '../services/agentService'
import { circleService, engineService, type RoleSkillChoice } from '../services/api'
import { SkillPicker } from '../components/SkillPicker'
import { signalingWsUrl } from '../config/env'
import { Save, ArrowLeft } from 'lucide-react'

function choicesFromNames(names: string[], catalog: Array<{ id: string; name: string; description: string }>): RoleSkillChoice[] {
  return names.map((name) => {
    const item = catalog.find((skill) => skill.name.toLowerCase() === name.toLowerCase())
    if (item) {
      return { catalogId: item.id, name: item.name, description: item.description }
    }
    return { catalogId: null, name, description: '' }
  })
}

function saveErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: { error?: unknown } } }).response
    const status = response?.status
    const payload = response?.data?.error
    const detail = typeof payload === 'string'
      ? payload
      : typeof payload === 'object' && payload !== null && 'message' in payload
        ? String((payload as { message: unknown }).message)
        : ''
    if (status === 404) {
      return 'La sauvegarde a été refusée (404). Recharge la page, puis réessaie.'
    }
    if (detail) return detail
    if (status) return `La sauvegarde a échoué (${status}).`
  }
  return 'La sauvegarde a échoué.'
}

export function AgentBuilder() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = Boolean(id)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [engineDraft, setEngineDraft] = useState<string | null>(null)
  const [skillDraft, setSkillDraft] = useState<RoleSkillChoice[] | null>(null)
  const manifests = useQuery('engine-manifests', () => engineService.manifests())
  const catalog = useQuery('skill-catalog', () => circleService.catalog())
  const declared = useQuery(
    ['agent-skills', id],
    () => circleService.declaredSkills({ agentId: id! }),
    { enabled: Boolean(id) },
  )
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '1.0.0',
    config: {
      agent: {
        name: '',
        description: '',
        llm: {
          provider: 'openai',
          model: 'gpt-4',
          temperature: 0.7,
          maxTokens: 2000
        },
        mcp: {
          enabled: false,
          tools: []
        },
        prompts: {
          system: 'Tu es un assistant IA utile et professionnel.',
          user: 'Comment puis-je t\'aider aujourd\'hui ?'
        },
        rtc: {
          enabled: true,
          // * Le repli 'ws://localhost:3000' était enregistré tel quel dans la
          // * configuration de l'agent : une fois déployé, l'agent cherchait le
          // * signaling dans son propre conteneur.
          signalingServer: signalingWsUrl()
        },
        docker: {
          image: 'agentteamforge/agent-base:latest',
          resources: {
            memory: '512Mi',
            cpu: '500m'
          }
        }
      }
    }
  })

  const { data: agent, isLoading } = useQuery(
    ['agent', id],
    () => agentService.getAgentById(id!),
    {
      enabled: isEditing,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            name: data.name,
            description: data.description,
            version: data.version,
            config: data.config
          })
        }
      }
    }
  )

  const engineName = engineDraft ?? agent?.engineName ?? ''
  const skills = skillDraft ?? choicesFromNames(declared.data ?? [], catalog.data ?? [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!engineName) {
      setSaveError('Choisissez un moteur. Sa version est figée au moment de l\'enregistrement.')
      return
    }
    setSaveError(null)
    try {
      const payload = { ...formData, engineName, skills: skills.map((skill) => skill.name) }
      if (isEditing) {
        await agentService.updateAgent(id!, payload)
      } else {
        await agentService.createAgent(payload)
      }
      navigate('/agents')
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      setSaveError(saveErrorMessage(error))
    }
  }

  const handleConfigChange = (path: string, value: any) => {
    setFormData(prev => {
      const newData = { ...prev }
      const keys = path.split('.')
      let current: any = newData.config
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      
      current[keys[keys.length - 1]] = value
      return newData
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="page-toolbar flex items-center justify-between">
        <div className="page-toolbar flex items-center space-x-4">
          <button
            type="button"
            onClick={() => navigate('/agents')}
            className="button button--secondary"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          <h1 className="text-3xl font-bold text-primary">
            {isEditing ? 'Modifier le modèle' : 'Nouveau modèle d\'agent'}
          </h1>
        </div>
      </div>

      {saveError && (
        <div className="p-4 rounded-lg bg-error-light text-error">
          {saveError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-medium text-primary mb-4">Informations générales</h2>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Nom de l'agent
              </label>
              <input id="agent-name" name="agent-name"
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    name: e.target.value,
                    config: {
                      ...prev.config,
                      agent: {
                        ...prev.config.agent,
                        name: e.target.value
                      }
                    }
                  }))
                }}
                className="form-input w-full"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Version
              </label>
              <input id="agent-version" name="agent-version"
                type="text"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                className="form-input w-full"
                required
              />
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-secondary mb-2">
              Description
            </label>
            <textarea id="agent-description" name="agent-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="form-input w-full"
            />
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-medium text-primary">Moteur et compétences</h2>
          <p className="text-sm text-secondary">
            Le moteur et sa version sont fixés ici. À l'instanciation, Core vérifie que cette version est disponible.
          </p>
          <label className="block text-sm font-medium text-secondary" htmlFor="agent-engine">
            Moteur
            <select
              id="agent-engine"
              name="agent-engine"
              className="form-input mt-1"
              value={engineName}
              onChange={(event) => setEngineDraft(event.target.value)}
              required
            >
              <option value="">Choisir</option>
              {(manifests.data ?? []).map((manifest) => (
                <option key={manifest.name} value={manifest.name}>
                  {manifest.name} {manifest.version}{manifest.compatible ? '' : ' (incompatible)'}
                </option>
              ))}
            </select>
          </label>
          {engineName && (
            <p className="text-sm text-secondary">
              Version figée : {(manifests.data ?? []).find((manifest) => manifest.name === engineName)?.version
                || agent?.engineVersion
                || 'celle installée sur ce Core, à l\'enregistrement'}
            </p>
          )}
          <div>
            <p className="text-sm font-medium text-secondary mb-2">Compétences du modèle</p>
            <SkillPicker
              catalog={catalog.data ?? []}
              value={skills}
              onChange={setSkillDraft}
            />
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-primary mb-4">Configuration LLM</h2>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Fournisseur
              </label>
              <select id="agent-llm-provider" name="agent-llm-provider"
                value={formData.config.agent.llm.provider}
                onChange={(e) => handleConfigChange('agent.llm.provider', e.target.value)}
                className="form-input w-full"
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="local">Local</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Modèle
              </label>
              <input id="agent-llm-model" name="agent-llm-model"
                type="text"
                value={formData.config.agent.llm.model}
                onChange={(e) => handleConfigChange('agent.llm.model', e.target.value)}
                className="form-input w-full"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Température
              </label>
              <input id="agent-llm-temperature" name="agent-llm-temperature"
                type="number"
                min="0"
                max="2"
                step="0.1"
                value={formData.config.agent.llm.temperature}
                onChange={(e) => handleConfigChange('agent.llm.temperature', parseFloat(e.target.value))}
                className="form-input w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Tokens maximum
              </label>
              <input id="agent-llm-max-tokens" name="agent-llm-max-tokens"
                type="number"
                min="1"
                max="4000"
                value={formData.config.agent.llm.maxTokens}
                onChange={(e) => handleConfigChange('agent.llm.maxTokens', parseInt(e.target.value))}
                className="form-input w-full"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-primary mb-4">Prompts</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Prompt système
              </label>
              <textarea id="agent-system-prompt" name="agent-system-prompt"
                value={formData.config.agent.prompts.system}
                onChange={(e) => handleConfigChange('agent.prompts.system', e.target.value)}
                rows={4}
                className="form-input w-full"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Prompt utilisateur
              </label>
              <textarea id="agent-user-prompt" name="agent-user-prompt"
                value={formData.config.agent.prompts.user}
                onChange={(e) => handleConfigChange('agent.prompts.user', e.target.value)}
                rows={2}
                className="form-input w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/agents')}
            className="button button--secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="button button--primary"
          >
            <Save className="w-5 h-5" />
            <span>{isEditing ? 'Mettre à jour' : 'Créer'}</span>
          </button>
        </div>
      </form>
    </div>
  )
}
